import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { AlertTriangle, Shield, Radar, ChevronRight, MessagesSquare } from "lucide-react";
import ChatAssistant from "@/components/ChatAssistant";

const STATUS_STYLES = {
  found:     { label: "FOUND",     cls: "border-[#FF3333] text-[#FF3333] bg-[#FF3333]/10" },
  requested: { label: "REQUESTED", cls: "border-[#E5FF00] text-[#E5FF00] bg-[#E5FF00]/10" },
  removed:   { label: "REMOVED",   cls: "border-[#00FF66] text-[#00FF66] bg-[#00FF66]/10" },
};

const SEV = { critical: "text-[#FF3333]", high: "text-orange-400", medium: "text-[#E5FF00]", low: "text-zinc-400" };

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) nav("/login");
  }, [user, authLoading, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scans/latest");
      setScan(data);
    } catch { setScan(null); } finally { setLoading(false); }
  };
  useEffect(() => { if (user) load(); }, [user]);

  const act = async (findingId, action) => {
    setBusy(findingId);
    try {
      await api.post("/scans/action", { finding_id: findingId, action });
      toast.success(action === "request_removal" ? "Removal request queued" : "Marked removed");
      await load();
    } catch { toast.error("Action failed"); } finally { setBusy(null); }
  };

  if (!user) return null;

  return (
    <div className="grain min-h-screen bg-[#050505] text-white">
      <Nav />
      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="mb-10 flex items-end justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E5FF00]">/ CONSOLE · {user.email}</div>
            <h1 className="mt-3 font-display text-4xl font-medium tracking-tighter md:text-5xl">Threat Console</h1>
          </div>
          <Button data-testid="btn-new-scan" onClick={() => nav("/scan")}
            className="btn-cta h-11 rounded-none border border-[#E5FF00] bg-transparent font-mono text-[11px] uppercase tracking-[0.25em] text-[#E5FF00] hover:bg-[#E5FF00] hover:text-black">
            Run New Scan →
          </Button>
        </div>

        {loading ? (
          <div className="border border-white/10 p-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
            Loading vault …
          </div>
        ) : !scan ? (
          <div className="border border-dashed border-white/15 p-16">
            <Radar className="mb-4 h-8 w-8 text-[#E5FF00]" strokeWidth={1.5} />
            <h2 className="font-display text-2xl font-medium tracking-tight">No scans yet.</h2>
            <p className="mt-2 max-w-md text-zinc-400">Run your first deep-scan to reveal where your data lives on the dark web.</p>
            <Link data-testid="empty-scan-link" to="/scan"
              className="mt-6 inline-block border border-[#E5FF00] bg-[#E5FF00] px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-black">
              Initialize Scan →
            </Link>
          </div>
        ) : (
          <>
            {/* Score bento */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className="col-span-1 border border-[#FF3333]/50 bg-[#FF3333]/[0.06] p-8 md:col-span-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF3333]">Exposure Score</div>
                <div className="mt-4 flex items-end gap-4">
                  <span data-testid="exposure-score" className="font-display text-7xl font-medium tracking-tighter text-[#FF3333]">{scan.exposure_score}</span>
                  <span className="mb-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">/ 99</span>
                </div>
                <div className="mt-6 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-[#FF3333]" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF3333]">{scan.risk_level} RISK</span>
                </div>
              </motion.div>

              <div className="col-span-1 border border-white/10 bg-black/40 p-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Findings</div>
                <div className="mt-4 font-display text-5xl font-medium tracking-tighter">{scan.findings.length}</div>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">brokers exposed</div>
              </div>

              <div className="col-span-1 border border-white/10 bg-black/40 p-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Removed</div>
                <div className="mt-4 font-display text-5xl font-medium tracking-tighter text-[#00FF66]">
                  {scan.findings.filter(f => f.status === "removed").length}
                </div>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">confirmed takedowns</div>
              </div>
            </div>

            {/* Findings table */}
            <div className="mt-8 border border-white/10">
              <div className="flex items-center justify-between border-b border-white/10 bg-black/40 px-6 py-4">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-[#E5FF00]" />
                  <h3 className="font-display text-lg font-medium tracking-tight">Data Broker Findings</h3>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Updated {new Date(scan.created_at).toLocaleString()}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] font-mono text-xs">
                  <thead className="border-b border-white/10 text-zinc-500">
                    <tr>
                      <th className="p-4 text-left uppercase tracking-[0.2em]">Broker</th>
                      <th className="p-4 text-left uppercase tracking-[0.2em]">Exposed Fields</th>
                      <th className="p-4 text-left uppercase tracking-[0.2em]">Severity</th>
                      <th className="p-4 text-left uppercase tracking-[0.2em]">Status</th>
                      <th className="p-4 text-right uppercase tracking-[0.2em]">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scan.findings.map((f, i) => {
                      const st = STATUS_STYLES[f.status];
                      return (
                        <tr key={f.id} data-testid={`finding-row-${i}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                          <td className="p-4">
                            <div className="text-sm text-white">{f.broker}</div>
                            <div className="text-[10px] text-zinc-600">{f.domain}</div>
                          </td>
                          <td className="p-4 text-zinc-400">{f.exposed_fields.join(", ")}</td>
                          <td className={`p-4 uppercase tracking-[0.15em] ${SEV[f.severity]}`}>{f.severity}</td>
                          <td className="p-4">
                            <Badge className={`rounded-none border ${st.cls} font-mono text-[10px] tracking-[0.15em]`}>{st.label}</Badge>
                          </td>
                          <td className="p-4 text-right">
                            {f.status === "found" && (
                              <button data-testid={`action-request-${i}`} disabled={busy === f.id}
                                onClick={() => act(f.id, "request_removal")}
                                className="inline-flex items-center gap-1 border border-white/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-300 hover:border-[#E5FF00] hover:text-[#E5FF00] transition-colors">
                                Request removal <ChevronRight className="h-3 w-3" />
                              </button>
                            )}
                            {f.status === "requested" && (
                              <button data-testid={`action-mark-${i}`} disabled={busy === f.id}
                                onClick={() => act(f.id, "mark_removed")}
                                className="inline-flex items-center gap-1 border border-[#00FF66]/40 bg-[#00FF66]/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#00FF66] hover:bg-[#00FF66]/20 transition-colors">
                                Mark removed <ChevronRight className="h-3 w-3" />
                              </button>
                            )}
                            {f.status === "removed" && (
                              <span className="text-[10px] uppercase tracking-[0.2em] text-[#00FF66]">[SEALED]</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Chat FAB */}
      <button data-testid="chat-toggle" onClick={() => setChatOpen(true)}
        className="btn-cta fixed bottom-6 right-6 z-40 flex items-center gap-2 border border-[#E5FF00] bg-black px-4 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#E5FF00] hover:bg-[#E5FF00] hover:text-black">
        <MessagesSquare className="h-4 w-4" /> Ask OBSIDIAN
      </button>
      {chatOpen && <ChatAssistant onClose={() => setChatOpen(false)} />}
    </div>
  );
}
